import {
  RefCountedAsyncResourceQueue,
  type ResourceSnapshot,
  type ResourceStatus,
} from "../_internal/RefCountedAsyncResourceQueue";
import { convertHeicToJpegUrl } from "./heicConverter";
import type {
  HeicConversionQueueOptions,
  HeicConversionSnapshot,
  HeicConversionStatus,
  HeicConversionSubscriber,
} from "./types";

/**
 * 기본 동시성 = 2.
 *
 * heic-to 가 메인 스레드에서 libheif WASM 디코딩 + canvas jpeg 인코딩을 수행하므로
 * 동시성을 너무 높이면 메인 스레드 jank 와 모바일 메모리 압박이 커진다.
 * 보수적으로 2 로 두고, 백그라운드 탭에서는 `pauseOnHidden` 으로 신규 작업을 멈춘다.
 */
const DEFAULT_CONCURRENCY = 2;
/**
 * HEIC → JPEG 변환은 메인 스레드 WASM 디코드 + canvas 인코드로 매우 비싸고,
 * 결과는 blob URL (수십~수백 KB) 로만 들고 있어 디코드된 비트맵 대비 메모리 부담이 작다.
 *
 * 상위 `ImageCache` 가 디코드된 비트맵을 LRU 관리하므로,
 * 큐 캐시는 "재변환을 피하기 위한 힌트 저장소" 역할로 충분히 크게 잡아 둔다.
 */
const DEFAULT_MAX_CACHE_SIZE = 80;

/**
 * 한 entry 의 생애 동안 허용하는 최대 변환 시도 횟수.
 *
 * 재 acquire 마다 자동 재시도하지만, 진짜로 손상된 HEIC 파일이나 영구적인 서버 오류를 만난 경우
 * 끝없이 재변환을 시도하는 것을 막는다. cap 도달 후 entry 는 status='error' 로 잠긴다.
 *
 * LRU evict 로 entry 가 사라지면 다음 acquire 는 fresh entry 가 되어 카운터가 0 부터 다시 시작.
 */
const MAX_CONVERSION_ATTEMPTS = 3;

/**
 * HEIC → JPEG 변환을 위한 프레임워크 비의존 큐.
 *
 * `RefCountedAsyncResourceQueue<string>` (= jpeg blob URL) 위에 얇은 어댑터로 구현.
 * 책임 (참조 카운팅 / LRU evict / pauseOnHidden / 재시도 캡 / 동시성 제한) 은 모두 base class 가 담당.
 * 이 클래스는 다음만 책임진다:
 * - 변환 함수 (`convertHeicToJpegUrl`) 주입
 * - 결과 정리 (`URL.revokeObjectURL`)
 * - 외부에 노출하는 status 명칭 (`converting` / `success` / ...) 매핑
 */
export class HeicConversionQueue {
  private readonly _queue: RefCountedAsyncResourceQueue<string>;

  constructor(options?: HeicConversionQueueOptions) {
    this._queue = new RefCountedAsyncResourceQueue<string>({
      run: options?.convert ?? convertHeicToJpegUrl,
      onCleanup: (_key, url) => {
        if (url) URL.revokeObjectURL(url);
      },
      concurrency: options?.concurrency ?? DEFAULT_CONCURRENCY,
      maxCacheSize: options?.maxCacheSize ?? DEFAULT_MAX_CACHE_SIZE,
      maxAttempts: MAX_CONVERSION_ATTEMPTS,
      getCacheKey: options?.getCacheKey,
      pauseOnHidden: options?.pauseOnHidden,
    });
  }

  peek(src: string): HeicConversionSnapshot | null {
    const snap = this._queue.peek(src);
    return snap ? toHeicSnapshot(snap) : null;
  }

  acquire(src: string, subscriber: HeicConversionSubscriber): () => void {
    return this._queue.acquire(src, (snap) => subscriber(toHeicSnapshot(snap)));
  }

  updateOptions(options: Partial<HeicConversionQueueOptions>): void {
    this._queue.updateOptions({
      ...(options.concurrency !== undefined && { concurrency: options.concurrency }),
      ...(options.maxCacheSize !== undefined && { maxCacheSize: options.maxCacheSize }),
      ...(options.getCacheKey !== undefined && { getCacheKey: options.getCacheKey }),
      ...(options.convert !== undefined && { run: options.convert }),
    });
  }

  destroy(): void {
    this._queue.destroy();
  }

  getStats(): {
    cacheSize: number;
    activeCount: number;
    pendingCount: number;
    concurrency: number;
    isHidden: boolean;
  } {
    return this._queue.getStats();
  }
}

/**
 * base 의 generic snapshot 을 외부 API 인 `HeicConversionSnapshot` 으로 변환한다.
 *
 * status 매핑: idle/running → "idle"/"converting", success → "success", error 동일.
 * url 은 success 일 때만 의미 있음.
 */
function toHeicSnapshot(snap: ResourceSnapshot<string>): HeicConversionSnapshot {
  return {
    src: snap.src,
    status: toHeicStatus(snap.status),
    url: snap.status === "success" ? snap.result : undefined,
    error: snap.error,
  };
}

function toHeicStatus(s: ResourceStatus): HeicConversionStatus {
  if (s === "running") return "converting";
  return s; // idle | success | error
}

/**
 * 모듈 싱글톤.
 *
 * 그리드 썸네일에서 변환된 결과를 라이트박스가 즉시 재사용할 수 있도록
 * 앱 전체가 동일한 캐시·동시성 제한을 공유한다.
 */
export const heicConversionQueue = new HeicConversionQueue();

export function createHeicConversionQueue(options?: HeicConversionQueueOptions): HeicConversionQueue {
  return new HeicConversionQueue(options);
}
